import React, { useEffect, useRef, useState } from 'react';
import { Eraser } from 'lucide-react';
import { Button } from '../../../components/ui/button';

interface Props {
    /** Fires with a PNG data URL, or null when cleared. */
    onChange: (dataUrl: string | null) => void;
    disabled?: boolean;
    height?: number;
}

/**
 * A drawn-signature capture surface (STOCK.md §12.2).
 *
 * Pointer events rather than separate mouse/touch handlers, so a finger on a phone and a
 * mouse on a desk take the same path — this is signed in a yard far more often than at
 * a desk.
 *
 * The canvas is sized to its rendered box multiplied by devicePixelRatio, otherwise the
 * signature is a blurry mess on exactly the phones that will be used to sign it.
 */
export const SignaturePad: React.FC<Props> = ({ onChange, disabled = false, height = 180 }) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const drawing = useRef(false);
    const [hasInk, setHasInk] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const resize = () => {
            const ratio = window.devicePixelRatio || 1;
            const rect = canvas.getBoundingClientRect();
            // Re-sizing clears the bitmap, so anything already drawn is lost. Acceptable:
            // a resize means an orientation change, and re-signing is one tap.
            canvas.width = rect.width * ratio;
            canvas.height = height * ratio;

            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            ctx.scale(ratio, ratio);
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.strokeStyle = '#0f172a';
        };

        resize();
        window.addEventListener('resize', resize);
        return () => window.removeEventListener('resize', resize);
    }, [height]);

    const positionOf = (e: React.PointerEvent<HTMLCanvasElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (disabled) return;
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx) return;
        // Capture the pointer so a stroke that leaves the canvas still ends cleanly.
        e.currentTarget.setPointerCapture(e.pointerId);
        drawing.current = true;
        const { x, y } = positionOf(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!drawing.current || disabled) return;
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx) return;
        const { x, y } = positionOf(e);
        ctx.lineTo(x, y);
        ctx.stroke();
        if (!hasInk) setHasInk(true);
    };

    const end = () => {
        if (!drawing.current) return;
        drawing.current = false;
        const canvas = canvasRef.current;
        if (canvas && hasInk) onChange(canvas.toDataURL('image/png'));
    };

    const clear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasInk(false);
        onChange(null);
    };

    return (
        <div className="space-y-2">
            <div className="relative border border-border rounded-lg bg-white overflow-hidden">
                <canvas
                    ref={canvasRef}
                    style={{ height, width: '100%', touchAction: 'none', cursor: disabled ? 'not-allowed' : 'crosshair' }}
                    onPointerDown={start}
                    onPointerMove={move}
                    onPointerUp={end}
                    onPointerCancel={end}
                />
                {!hasInk && (
                    <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                        Sign here
                    </span>
                )}
            </div>
            <div className="flex justify-end">
                <Button type="button" variant="outline" size="sm" onClick={clear} disabled={disabled || !hasInk}>
                    <Eraser size={14} className="mr-1.5" />Clear
                </Button>
            </div>
        </div>
    );
};

export default SignaturePad;
